import { useEffect, useState, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import NotFound from "@/pages/NotFound";

const STORAGE_KEY = "installation-link-key";

// Static link gate for the medatixx guide: the link inside medatixx carries
// ?k=<key>; the key is verified server side by installation-link. Missing or
// wrong keys render NotFound so the page is not discoverable.
export function InstallationLinkGate({ children }: { children: ReactNode }) {
  const [params] = useSearchParams();
  const [state, setState] = useState<"checking" | "ok" | "denied">("checking");

  useEffect(() => {
    const key = params.get("k") || sessionStorage.getItem(STORAGE_KEY) || "";
    if (!key) {
      setState("denied");
      return;
    }
    supabase.functions
      .invoke<{ ok: boolean }>("installation-link", { body: { key } })
      .then(({ data, error }) => {
        if (error || !data?.ok) {
          sessionStorage.removeItem(STORAGE_KEY);
          setState("denied");
        } else {
          sessionStorage.setItem(STORAGE_KEY, key);
          setState("ok");
        }
      })
      .catch(() => setState("denied"));
  }, [params]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  if (state === "checking") return null;
  if (state === "denied") return <NotFound />;
  return <>{children}</>;
}
